import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Centralised user preferences — persists to localStorage so reloads
 * don't reset language / timezone / unit choices. Everything that
 * formats a value (distances, speeds, times) should read from here so
 * a single user toggle propagates everywhere.
 */

export type LanguageCode = 'ar' | 'en';
export type DistanceUnit = 'km' | 'mi';
export type SpeedUnit = 'kmh' | 'mph';

interface SettingsState {
  language: LanguageCode;
  timezone: string;
  distanceUnit: DistanceUnit;
  speedUnit: SpeedUnit;

  setLanguage: (l: LanguageCode) => void;
  setTimezone: (t: string) => void;
  setDistanceUnit: (u: DistanceUnit) => void;
  setSpeedUnit: (u: SpeedUnit) => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      language: 'ar',
      timezone: 'Africa/Tripoli',
      distanceUnit: 'km',
      speedUnit: 'kmh',

      setLanguage: (language) => {
        set({ language });
        applyLanguage(language);
      },
      setTimezone: (timezone) => {
        set({ timezone });
        dayjs.tz.setDefault(timezone);
      },
      setDistanceUnit: (distanceUnit) => set({ distanceUnit }),
      setSpeedUnit: (speedUnit) => set({ speedUnit }),
    }),
    {
      name: 'app-settings',
      onRehydrateStorage: () => (state) => {
        // Apply persisted prefs the moment we hydrate, so the very
        // first render after reload already reflects them.
        if (state) {
          applyLanguage(state.language);
          dayjs.tz.setDefault(state.timezone);
        }
      },
    },
  ),
);

// Apply the language to the document — RTL/LTR + html lang attribute —
// without touching React state. Called from the store on every change
// and on rehydrate.
function applyLanguage(lang: LanguageCode) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
}

// ── Conversion helpers ──────────────────────────────────────────────
// Inputs from the backend are always km / km/h (the Teltonika SDK
// reports SI). These helpers convert at the display boundary only.

const MI_PER_KM = 0.621371;

export const formatDistance = (km: number | null | undefined, unit: DistanceUnit): string => {
  if (km == null || !Number.isFinite(km)) return '—';
  if (unit === 'mi') {
    const mi = km * MI_PER_KM;
    return `${mi.toFixed(1)} ميل`;
  }
  return `${km.toFixed(1)} كم`;
};

export const formatSpeed = (kmh: number | null | undefined, unit: SpeedUnit): string => {
  if (kmh == null || !Number.isFinite(kmh)) return '—';
  if (unit === 'mph') {
    const mph = kmh * MI_PER_KM;
    return `${mph.toFixed(0)} م/س`;
  }
  return `${kmh.toFixed(0)} كم/س`;
};

// Format a timestamp in the user's chosen timezone. Falls back gracefully
// if the input is null/undefined so we don't render "Invalid Date" rows.
export const formatTime = (
  input: string | Date | null | undefined,
  tz: string,
  pattern = 'HH:mm:ss',
): string => {
  if (!input) return '—';
  const d = dayjs(input);
  if (!d.isValid()) return '—';
  return d.tz(tz).format(pattern);
};
